import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInputChange } from "hooks/useInputChange";
import { typePaginationDashboardObject } from "../DashboardContentTable/PaginationDashboard/PaginationDashboard";
import {
  IdSalesState,
  typeSalesFilters,
} from "model/dashboardSales/dSalesReducer";
import {
  dSalesClearFiltersAC,
  dSalesExportDataSagaAC,
  dSalesSelectedExportDataSagaAC,
  dSalesSetDataSagaAC,
  dSalesSetDetailItemAC,
  dSalesSetFiltersAC,
  dSalesSetItemsForPageAC,
  dSalesSetPageNumberAC,
} from "model/dashboardSales/dSalesActionCreators";
import { ViewDataSales } from "components/containers/dashboard/dataSales/ViewComponent";
import { Trans } from "@lingui/macro";
import { ISystemState } from "model/system/systemReducer";
import { languageForUrl } from "locales/languages";

// Type
export type typeSales = {
  variant?: string;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export type typeDataSalesRowActions = {
  title: React.JSX.Element;
  action: (id: string) => void;
}[];
export type typeDataSalesBulkActions = {
  title: React.JSX.Element;
  action: () => void;
  suggest: React.JSX.Element;
}[];

// Component
export const DataSales: React.FC<typeSales> = ({ variant, setSidebarOpen }) => {
  const dispatch = useDispatch();

  // get lang from store
  const lang = useSelector(
    (state: { system: ISystemState }) => state.system.language,
  );

  // Data list
  const dataList = useSelector(
    (state: { dSales: IdSalesState }) => state.dSales.data,
  );

  // DateFilter
  const [isActiveFilters, setIsActiveFilters] = useState(false);
  const [openFilterBar, setOpenFilterBar] = useState(false);
  const filters = useSelector(
    (state: { dSales: IdSalesState }) => state.dSales.filters,
  );

  // Pagination
  const pagination: typePaginationDashboardObject = {
    paginationObject: useSelector(
      (state: { dSales: IdSalesState }) => state.dSales.pagination,
    ),
    onGoToPage: (id) => dispatch(dSalesSetPageNumberAC(id)),
    onSelectItems: (itemsPerPage) =>
      dispatch(dSalesSetItemsForPageAC(itemsPerPage)),
  };

  // Did mount & Did unmount
  useEffect(() => {
    return () => {
      dispatch(dSalesClearFiltersAC());
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(
      dSalesSetDataSagaAC(filters, {
        page: pagination.paginationObject.pageNumber,
        size: pagination.paginationObject.itemsPerPage!,
      }),
    );
    // eslint-disable-next-line
  }, [
    filters,
    pagination.paginationObject.pageNumber,
    pagination.paginationObject.itemsPerPage,
  ]);

  // Search panel

  const searchStoreName = useSelector(
    (state: { dSales: IdSalesState }) => state.dSales.filters?.storeName,
  );

  const {
    value: searchValue,
    setValue: setSearchValue,
    changeValueHandler: onChangeSearchValue,
  } = useInputChange(searchStoreName || "");
  const onClearSearch = () => {
    dispatch(
      dSalesSetFiltersAC({
        storeName: null,
      }),
    );

    setSearchValue("");
  };
  const onEnterSearch = () => {
    const filters: typeSalesFilters = {
      storeName: searchValue.trim(),
    };
    dispatch(dSalesSetFiltersAC(filters));
  };

  // Popup Print
  const [openPopupPrint, setOpenPopupPrint] = useState(false);

  // Popup details
  const [openPopupDetails, setOpenPopupDetails] = useState(false);

  // Make table item actions
  const rowsActions: typeDataSalesRowActions = [
    {
      title: <Trans>Print</Trans>,
      action: (id: string) => {
        dispatch(dSalesSetDetailItemAC(id));
        setOpenPopupPrint(true);
      },
    },
  ];
  // Make bulk actions
  const bulkActions: typeDataSalesBulkActions = [
    {
      title: <Trans>Selected export</Trans>,
      action: () =>
        dispatch(
          dSalesSelectedExportDataSagaAC(
            dataList.filter((item) => item.checked).map((item) => item.id),
            languageForUrl[lang],
          ),
        ),
      suggest: <Trans>Export checked sales only</Trans>,
    },
  ];

  // Print dialog actions
  const cancelPrint = () => {
    setOpenPopupPrint(false);
  };
  const printHandler = () => {
    setOpenPopupPrint(false);
  };
  // export with filters && lang
  const onClickExport = () => {
    dispatch(dSalesExportDataSagaAC(filters, languageForUrl[lang]));
  };

  return (
    <ViewDataSales
      variant={variant}
      setSidebarOpen={setSidebarOpen}
      openFilterBar={openFilterBar}
      setOpenFilterBar={setOpenFilterBar}
      openPopupDetails={openPopupDetails}
      setOpenPopupDetails={setOpenPopupDetails}
      openPopupPrint={openPopupPrint}
      setOpenPopupPrint={setOpenPopupPrint}
      onCancelPrint={cancelPrint}
      onPrintSubmitClick={printHandler}
      searchValue={searchValue}
      onChangeSearchValue={onChangeSearchValue}
      onEnterSearch={onEnterSearch}
      onClearSearch={onClearSearch}
      rowsActions={rowsActions}
      bulkActions={bulkActions}
      onClickExport={onClickExport}
      pagination={pagination}
      isActiveFilters={isActiveFilters}
      setIsActiveFilters={setIsActiveFilters}
    />
  );
};
